import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { User, Search, Plus, Eye, RefreshCw, X } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const AideDocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '', patientName: '', documentType: 'Aide Care Plan',
    visitDate: new Date().toISOString().split('T')[0], aideName: '', notes: '', status: 'Active'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [docsRes, patientsRes] = await Promise.all([
        api.get('/visit-documents/type/Aide'),
        api.get('/patients')
      ]);
      if (docsRes.data.success) setDocuments(docsRes.data.documents || []);
      if (patientsRes.data.success) setPatients(patientsRes.data.patients || []);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.patientName) { toast.error('Please select a patient'); return; }
    try {
      const res = await api.post('/visit-documents', { ...formData, discipline: 'Aide' });
      if (res.data.success) {
        toast.success('Aide document created');
        setDocuments(prev => [...prev, res.data.document]);
        setShowModal(false);
        setFormData({ patientId: '', patientName: '', documentType: 'Aide Care Plan', visitDate: new Date().toISOString().split('T')[0], aideName: '', notes: '', status: 'Active' });
      }
    } catch (error) {
      toast.error('Failed to create document');
    }
  };

  const filteredDocs = documents.filter(d =>
    d.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.documentType?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    activePatients: patients.length,
    totalDocs: documents.length,
    completed: documents.filter(d => d.status === 'Complete' || d.status === 'Completed').length,
    inProgress: documents.filter(d => d.status === 'In Progress' || d.status === 'Active').length
  };

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="aide-documents-page">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <User className="w-6 h-6 text-teal-600" />
                Aide Documents
              </h1>
              <p className="text-gray-500 text-sm mt-1">Manage home health aide care plans, task sheets, and visit notes</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
              <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />New Aide Document
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-teal-700">{stats.activePatients}</div>
            <div className="text-sm text-teal-600">Active Aide Patients</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.totalDocs}</div>
            <div className="text-sm text-blue-600">Total Documents</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.completed}</div>
            <div className="text-sm text-green-600">Completed</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-700">{stats.inProgress}</div>
            <div className="text-sm text-yellow-600">In Progress</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by patient or document type..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No aide documents found. Click "New Aide Document" to create one.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Document Type</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Aide</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredDocs.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{doc.patientName}</td>
                    <td className="px-6 py-4">{doc.documentType}</td>
                    <td className="px-6 py-4">{doc.visitDate}</td>
                    <td className="px-6 py-4">{doc.aideName || '-'}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${doc.status === 'Complete' || doc.status === 'Completed' ? 'bg-green-100 text-green-800' : doc.status === 'Active' ? 'bg-teal-100 text-teal-800' : 'bg-yellow-100 text-yellow-800'}`}>
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Button size="sm" variant="outline"><Eye className="w-4 h-4 mr-1" />View</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">New Aide Document</h2>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Patient *</label>
                  <select value={formData.patientId} onChange={(e) => {
                    const p = patients.find(p => p.id === e.target.value);
                    setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: p ? `${p.firstName} ${p.lastName}` : '' }));
                  }} className="w-full border rounded-md px-3 py-2">
                    <option value="">Select Patient</option>
                    {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Document Type</label>
                  <select value={formData.documentType} onChange={(e) => setFormData(prev => ({ ...prev, documentType: e.target.value }))} className="w-full border rounded-md px-3 py-2">
                    <option>Aide Care Plan</option>
                    <option>Aide Visit Note</option>
                    <option>Aide Task Sheet</option>
                    <option>Aide Supervisory Visit</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Aide Name</label>
                  <Input value={formData.aideName} onChange={(e) => setFormData(prev => ({ ...prev, aideName: e.target.value }))} placeholder="e.g., Maria Garcia, HHA" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Visit Date</label>
                  <Input type="date" value={formData.visitDate} onChange={(e) => setFormData(prev => ({ ...prev, visitDate: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={3} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button className="bg-teal-600 hover:bg-teal-700 text-white" onClick={handleSubmit}>Create</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AideDocumentsPage;
